import { initContract } from '@ts-rest/core';
import { z } from 'zod';
const c = initContract();
export const SyncRunKindSchema = z.enum(['lines', 'organization-details']);
export const SyncRunSchema = z.object({
    id: z.string(),
    kind: z.string(),
    scope: z.string(),
    organizationId: z.string().nullable(),
    organizationName: z.string().nullable(),
    externalOrganizationId: z.string().nullable(),
    upserted: z.number(),
    softDeleted: z.number(),
    errors: z.number(),
    durationMs: z.number(),
    status: z.string(),
    errorMessage: z.string().nullable(),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
});
export const LinesSyncHealthSchema = z.object({
    totalLines: z.number(),
    staleLines: z.number(),
    staleThresholdHours: z.number(),
    oldestLastSyncedAt: z.string().nullable(),
});
export const SyncOverviewSchema = z.object({
    linkedOrganizations: z.number(),
    totalSites: z.number(),
    lines: LinesSyncHealthSchema,
    lastRuns: z.object({
        lines: SyncRunSchema.nullable(),
        organizationDetails: SyncRunSchema.nullable(),
    }),
});
export const OrganizationSyncStatsSchema = z.object({
    organizationId: z.string(),
    organizationName: z.string(),
    externalOrganizationId: z.string(),
    siteCount: z.number(),
    totalLines: z.number(),
    lastLinesSyncedAt: z.string().nullable(),
    lastDetailsSyncedAt: z.string().nullable(),
    lastDetailsStatus: z.string().nullable(),
});
export const LinesSyncResultSchema = z.object({
    organizationId: z.string(),
    upserted: z.number(),
    softDeleted: z.number(),
    errors: z.number(),
    durationMs: z.number(),
});
export const LinesFullSyncResultSchema = z.object({
    organizationsCount: z.number(),
    upserted: z.number(),
    softDeleted: z.number(),
    errors: z.number(),
    failedOrganizations: z.number(),
    durationMs: z.number(),
});
export const DetailsSyncResultSchema = z.object({
    organizationNameUpdated: z.boolean(),
    sitesUpdated: z.number(),
    sitesCreated: z.number(),
});
export const DetailsFullSyncResultSchema = z.object({
    organizationsCount: z.number(),
    organizationNamesUpdated: z.number(),
    sitesUpdated: z.number(),
    sitesCreated: z.number(),
    failedOrganizations: z.number(),
    durationMs: z.number(),
});
export const RunSyncBodySchema = z.object({
    organizationId: z.string().min(1, 'organizationId is required'),
});
/**
 * Admin-only monitoring and triggers for the Sewan → local syncs.
 * The metier front does not know sync exists.
 */
export const adminSyncContract = c.router({
    getSyncOverview: {
        method: 'GET',
        path: '/admin/sync/overview',
        responses: { 200: SyncOverviewSchema },
        summary: 'Global sync overview cards',
    },
    getSyncHistory: {
        method: 'GET',
        path: '/admin/sync/history',
        query: z.object({
            limit: z.coerce.number().int().positive().max(200).optional(),
            kind: SyncRunKindSchema.optional(),
        }),
        responses: { 200: z.array(SyncRunSchema) },
        summary: 'Unified run history (lines + organization details)',
    },
    getLinesSyncHealth: {
        method: 'GET',
        path: '/admin/sync/lines/health',
        responses: { 200: LinesSyncHealthSchema },
        summary: 'Staleness health of the mobile line sync',
    },
    runLinesSync: {
        method: 'POST',
        path: '/admin/sync/lines/run',
        body: RunSyncBodySchema,
        responses: { 201: LinesSyncResultSchema },
        summary: 'Sync the mobile lines of one organization',
    },
    runLinesFullSync: {
        method: 'POST',
        path: '/admin/sync/lines/run-all',
        body: c.noBody(),
        responses: { 201: LinesFullSyncResultSchema },
        summary: 'Sync the mobile lines of every organization',
    },
    getOrganizationsSyncStats: {
        method: 'GET',
        path: '/admin/sync/organizations/stats',
        responses: { 200: z.array(OrganizationSyncStatsSchema) },
        summary: 'Per-organization sync stats',
    },
    runDetailsSync: {
        method: 'POST',
        path: '/admin/sync/organizations/run',
        body: RunSyncBodySchema,
        responses: { 201: DetailsSyncResultSchema },
        summary: 'Pull the details of one organization from Sewan',
    },
    runDetailsFullSync: {
        method: 'POST',
        path: '/admin/sync/organizations/run-all',
        body: c.noBody(),
        responses: { 201: DetailsFullSyncResultSchema },
        summary: 'Pull the details of every organization from Sewan',
    },
});
